/**
 * Point - 3D Point geometry class
 *
 * Represents a point in 3D space with position and attributes.
 */

import { GeometryBase, GeometryType, generateId } from './GeometryBase';
import { Vector } from './Vector';

export class Point extends GeometryBase {
  /**
   * Create a Point
   * @param {number} x - X coordinate (mm)
   * @param {number} y - Y coordinate (mm)
   * @param {number} z - Z coordinate (mm)
   * @param {Object} attributes - Optional attributes
   */
  constructor(x = 0, y = 0, z = 0, attributes = {}) {
    super(GeometryType.POINT, 'pt', attributes);
    this.position = new Vector(x, y, z);
  }

  // Convenience getters
  get x() { return this.position.x; }
  get y() { return this.position.y; }
  get z() { return this.position.z; }

  // Setters that update position
  set x(val) { this.position = new Vector(val, this.y, this.z); this.touch(); }
  set y(val) { this.position = new Vector(this.x, val, this.z); this.touch(); }
  set z(val) { this.position = new Vector(this.x, this.y, val); this.touch(); }

  /**
   * Move point to new position
   * @param {number} x - New X coordinate
   * @param {number} y - New Y coordinate
   * @param {number} z - New Z coordinate
   */
  moveTo(x, y, z) {
    this.position = new Vector(x, y, z);
    this.touch();
  }

  /**
   * Move point by delta
   * @param {number} dx - Delta X
   * @param {number} dy - Delta Y
   * @param {number} dz - Delta Z
   */
  moveBy(dx, dy, dz = 0) {
    this.position = this.position.add(new Vector(dx, dy, dz));
    this.touch();
  }

  /**
   * Distance to another point
   * @param {Point} other - Other point
   * @returns {number} Distance in mm
   */
  distanceTo(other) {
    return this.position.distanceTo(other.position);
  }

  /**
   * Distance to position
   * @param {number} x
   * @param {number} y
   * @param {number} z
   * @returns {number} Distance in mm
   */
  distanceToPosition(x, y, z = 0) {
    return this.position.distanceTo(new Vector(x, y, z));
  }

  /**
   * Vector from this point to another
   * @param {Point} other - Target point
   * @returns {Vector} Direction vector
   */
  vectorTo(other) {
    return other.position.subtract(this.position);
  }

  /**
   * Midpoint between this and another point
   * @param {Point} other - Other point
   * @returns {Point} Midpoint
   */
  midpointTo(other) {
    const mid = this.position.lerp(other.position, 0.5);
    return new Point(mid.x, mid.y, mid.z);
  }

  // Transform implementations
  clone() {
    const p = new Point(this.x, this.y, this.z, { ...this.attributes });
    return p;
  }

  transform({ translate, rotate, scale, mirror }) {
    let pos = this.position.clone();

    // Apply scale first (around origin)
    if (scale) {
      if (typeof scale === 'number') {
        pos = pos.multiply(scale);
      } else {
        pos = pos.scale(scale.x, scale.y, scale.z);
      }
    }

    // Apply rotation (around origin in XY plane)
    if (rotate) {
      pos = pos.rotate2D(rotate);
    }

    // Apply translation last
    if (translate) {
      pos = pos.add(Vector.fromObject(translate));
    }

    // Apply mirror
    if (mirror) {
      pos = pos.mirror(Vector.fromObject(mirror));
    }

    return new Point(pos.x, pos.y, pos.z, { ...this.attributes });
  }

  getBoundingBox() {
    return {
      min: { x: this.x, y: this.y, z: this.z },
      max: { x: this.x, y: this.y, z: this.z },
    };
  }

  toJSON() {
    return {
      ...super.toJSON(),
      position: this.position.toObject(),
    };
  }

  static fromJSON(json) {
    const p = new Point(
      json.position.x,
      json.position.y,
      json.position.z,
      json.attributes
    );
    p.id = json.id;
    p.createdAt = json.createdAt;
    p.modifiedAt = json.modifiedAt;
    return p;
  }

  /**
   * Create Point from legacy point format
   * @param {Object} legacyPoint - Old point format {id, x, y, z, type}
   * @returns {Point}
   */
  static fromLegacy(legacyPoint) {
    const p = new Point(
      legacyPoint.x,
      legacyPoint.y,
      legacyPoint.z || 0,
      { legacyType: legacyPoint.type }
    );
    // Preserve original ID for migration
    p.id = legacyPoint.id;
    return p;
  }
}

export default Point;
